import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Download, FileText, Clock, X, Loader2, CheckCircle2, AlertCircle, Sparkles, Target, MessageSquare, CalendarClock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { apiRequest } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { recapBullets, actionItems } from '@/lib/postCallRecap';
import type { VoiceCall } from '../../../../shared/schema';

/**
 * Browser / power-dialer calls come back from /api/voice-calls/:sid with the
 * AI's suggested next step and the rep's saved follow-up. AI-agent calls don't
 * carry these, so the next-step panel only shows for `source: 'calls'`.
 */
type CallView = VoiceCall & {
  source?: 'calls';
  recordingSid?: string | null;
  aiNextStep?: { text: string; dueDate: string | null; owner: 'rep' | 'prospect' | null } | null;
  followUpNotes?: string | null;
  followUpDate?: string | null;
};

const CRM_RESULT: Record<string, string> = {
  logged: 'Logged to HubSpot.',
  no_lead: 'Saved. This call has no contact to log it against.',
  not_connected: 'Saved. Connect HubSpot in Integrations to log calls there.',
  no_contact: 'Saved. No matching HubSpot contact was found.',
  failed: 'Saved here, but HubSpot rejected the note.',
};

interface PostCallSummaryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callSid: string | null;
}

export function PostCallSummaryDialog({ isOpen, onClose, callSid }: PostCallSummaryDialogProps) {
  const [callDetails, setCallDetails] = useState<CallView | null>(null);
  const [nextStepText, setNextStepText] = useState('');
  const [nextStepDue, setNextStepDue] = useState('');
  const [nextStepTouched, setNextStepTouched] = useState(false);
  const [savingNextStep, setSavingNextStep] = useState<'save' | 'log' | null>(null);
  const [savedNextStep, setSavedNextStep] = useState(false);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Same cache the dialer uses; only to put the contact's name in the title.
  const { data: rawLeads } = useQuery<any>({ queryKey: ['/api/leads'], enabled: isOpen, staleTime: 60000 });

  useEffect(() => {
    if (!isOpen || !callSid) {
      setCallDetails(null);
      setIsLoading(true);
      setError(null);
      setNextStepText('');
      setNextStepDue('');
      setNextStepTouched(false);
      setSavedNextStep(false);
      return;
    }

    let isActive = true;

    const fetchCallDetails = async (showLoading = true) => {
      try {
        if (showLoading) {
          setIsLoading(true);
        }
        setError(null);
        
        const data = await apiRequest('GET', `/api/voice-calls/${callSid}`);
        if (isActive) {
          setCallDetails(data);
        }
        return data;
      } catch (err) {
        console.error('Error fetching call details:', err);
        if (isActive) {
          setError('Unable to load call details');
        }
        return null;
      } finally {
        if (showLoading && isActive) {
          setIsLoading(false);
        }
      }
    };

    // Fetch with retry for webhook timing race - call record may not exist yet
    let retryCount = 0;
    const maxRetries = 5;

    const fetchWithRetry = async () => {
      const data = await fetchCallDetails(retryCount === 0);
      if (!data && retryCount < maxRetries && isActive) {
        retryCount++;
        const delay = Math.min(1000 * Math.pow(1.5, retryCount), 5000);
        setTimeout(fetchWithRetry, delay);
      }
    };

    fetchWithRetry();

    // Set up polling for AI analysis status. Capped: a call with no
    // recording never gets a status, and polling it forever is just load.
    let polls = 0;
    const pollInterval = setInterval(async () => {
      const freshData = await fetchCallDetails(false);
      if (++polls >= 40 || (freshData && (freshData.aiAnalysisStatus === 'completed' || freshData.aiAnalysisStatus === 'failed'))) {
        clearInterval(pollInterval);
      }
    }, 3000);

    return () => {
      isActive = false;
      clearInterval(pollInterval);
    };
  }, [isOpen, callSid]);

  // Prefill from the rep's saved follow-up, else the AI's suggestion, until
  // the rep starts typing (polling must not overwrite their edits).
  useEffect(() => {
    if (!callDetails || nextStepTouched) return;
    const saved = callDetails.followUpNotes;
    setNextStepText(saved || callDetails.aiNextStep?.text || '');
    setNextStepDue(
      saved && callDetails.followUpDate
        ? String(callDetails.followUpDate).slice(0, 10)
        : callDetails.aiNextStep?.dueDate || '',
    );
  }, [callDetails, nextStepTouched]);

  const saveNextStep = async (logToCrm: boolean) => {
    if (!callDetails) return;
    setSavingNextStep(logToCrm ? 'log' : 'save');
    try {
      const res = await apiRequest('POST', `/api/calls/${callDetails.id}/next-step`, {
        text: nextStepText,
        dueDate: nextStepDue || null,
        logToCrm,
      });
      setSavedNextStep(true);
      toast({ title: 'Next step saved', description: logToCrm ? CRM_RESULT[res?.crm] : undefined });
    } catch (err) {
      console.error('Error saving next step:', err);
      toast({ title: 'Could not save the next step', variant: 'destructive' });
    } finally {
      setSavingNextStep(null);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleRegenerateAnalysis = async () => {
    if (!callDetails || !callSid) return;
    
    try {
      setIsRegenerating(true);
      setError(null);
      
      // No /reanalyze route exists. regenerate-summary is the real one and
      // resolves a call by its numeric id, not its SID.
      await apiRequest('POST', `/api/calls/${callDetails.id}/regenerate-summary`);
      
      // Update status to processing immediately
      setCallDetails(prev => prev ? { ...prev, aiAnalysisStatus: 'processing' } : prev);
      
      // Polling will automatically pick up the processing status and fetch updates
      
    } catch (err) {
      console.error('Error regenerating analysis:', err);
      setError('Failed to regenerate analysis. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const otherNumber = callDetails ? (callDetails.direction === 'inbound' ? callDetails.fromNumber : callDetails.toNumber) || callDetails.fromNumber : '';
  const leadList: any[] = Array.isArray(rawLeads) ? rawLeads : rawLeads?.data || rawLeads?.results || [];
  const otherDigits = String(otherNumber || '').replace(/\D/g, '');
  const lead = callDetails
    ? leadList.find((l: any) => ((callDetails as any).leadId && l.id === (callDetails as any).leadId) || (otherDigits && String(l.phone || '').replace(/\D/g, '') === otherDigits))
    : undefined;
  const contactLabel = (lead ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim() : '') || otherNumber;
  const bullets = callDetails ? recapBullets(callDetails.summary, callDetails.aiKeyPoints) : [];
  // Action items: the reviewed next step is item 1 (editable); the analyzer's other items follow.
  const extraItems = callDetails ? actionItems('', callDetails.aiActionItems).filter((t) => t.toLowerCase() !== nextStepText.trim().toLowerCase()) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl" data-testid="text-recap-title">Call with {contactLabel || 'contact'}</DialogTitle>
              <DialogDescription>
                {callDetails ? `${otherNumber}${callDetails.duration ? ` · ${formatDuration(callDetails.duration)}` : ''}` : 'Post-call recap'}
              </DialogDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="button-close-summary">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="ml-3 text-muted-foreground">Loading call details...</p>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {!isLoading && !error && callDetails && (
          <div className="space-y-4">
            {/* Call Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge className="mt-1" variant={callDetails.status === 'completed' ? 'default' : 'secondary'}>
                  {callDetails.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Duration</p>
                <div className="mt-1 flex items-center space-x-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm font-medium">{formatDuration(callDetails.duration)}</p>
                </div>
              </div>
            </div>

            {/* AI Analysis Status */}
            {callDetails.aiAnalysisStatus && (
              <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">AI Analysis</span>
                </div>
                <Badge variant={
                  callDetails.aiAnalysisStatus === 'completed' ? 'default' :
                  callDetails.aiAnalysisStatus === 'failed' ? 'destructive' :
                  callDetails.aiAnalysisStatus === 'processing' ? 'secondary' : 'outline'
                }>
                  {callDetails.aiAnalysisStatus === 'completed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                  {callDetails.aiAnalysisStatus === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                  {callDetails.aiAnalysisStatus === 'processing' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  {callDetails.aiAnalysisStatus}
                </Badge>
              </div>
            )}

            {/* Recap: 3-5 bullets from the stored AI summary (+ key points) */}
            {bullets.length > 0 && (
              <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid="recap-section">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  <h3 className="font-semibold">Recap</h3>
                </div>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                  {bullets.map((t, i) => <li key={i} data-testid={`recap-bullet-${i}`}>{t}</li>)}
                </ul>
              </section>
            )}

            {/* Action items: next step (editable, saved / logged by the rep) + the analyzer's items */}
            {(callDetails.source === 'calls' && (callDetails.aiAnalysisStatus === 'completed' || callDetails.followUpNotes)) || extraItems.length > 0 ? (
              <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm space-y-3" data-testid="next-step-panel">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">Action items</h3>
                  {callDetails.aiNextStep && !callDetails.followUpNotes && (
                    <Badge variant="secondary" className="ml-auto">
                      <Sparkles className="w-3 h-3 mr-1" />AI suggested{callDetails.aiNextStep.owner === 'prospect' ? ' · their action' : ''}
                    </Badge>
                  )}
                </div>
                <ol className="space-y-2.5">
                  {callDetails.source === 'calls' && (callDetails.aiAnalysisStatus === 'completed' || callDetails.followUpNotes) && (
                    <li className="flex gap-2">
                      <span className="mt-2 w-5 shrink-0 text-sm font-semibold text-slate-400 tabular-nums">1.</span>
                      <div className="flex-1 space-y-2">
                        <Textarea
                          value={nextStepText}
                          onChange={(e) => { setNextStepTouched(true); setSavedNextStep(false); setNextStepText(e.target.value); }}
                          placeholder={callDetails.aiNextStep ? '' : 'No next step was agreed on the call. Add one?'}
                          rows={2}
                          aria-label="Next step"
                          data-testid="input-next-step"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <CalendarClock className="w-4 h-4 text-slate-400" />
                          <Input
                            type="date"
                            value={nextStepDue}
                            onChange={(e) => { setNextStepTouched(true); setSavedNextStep(false); setNextStepDue(e.target.value); }}
                            className="w-44"
                            aria-label="Due date"
                            data-testid="input-next-step-due"
                          />
                        </div>
                      </div>
                    </li>
                  )}
                  {extraItems.map((item, i) => {
                    const n = i + (callDetails.source === 'calls' && (callDetails.aiAnalysisStatus === 'completed' || callDetails.followUpNotes) ? 2 : 1);
                    return (
                      <li key={i} className="flex gap-2 text-sm text-slate-700" data-testid={`action-item-${i}`}>
                        <span className="w-5 shrink-0 font-semibold text-slate-400 tabular-nums">{n}.</span>
                        <span>{item}</span>
                      </li>
                    );
                  })}
                </ol>
                {callDetails.source === 'calls' && (callDetails.aiAnalysisStatus === 'completed' || callDetails.followUpNotes) && (
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" disabled={!!savingNextStep} onClick={() => saveNextStep(false)} data-testid="button-save-next-step">
                      {savingNextStep === 'save' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      {savedNextStep ? 'Saved' : 'Save'}
                    </Button>
                    <Button size="sm" disabled={!!savingNextStep} onClick={() => saveNextStep(true)} data-testid="button-log-next-step">
                      {savingNextStep === 'log' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      Save &amp; log to CRM
                    </Button>
                  </div>
                )}
              </section>
            ) : null}

            {/* Analysis Metadata */}
            {(callDetails.aiTone || callDetails.aiIntent || callDetails.aiUrgency || callDetails.aiTopics) && (
              <div className="grid grid-cols-2 gap-3">
                {callDetails.sentiment && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Sentiment:</span>
                    <Badge variant={
                      callDetails.sentiment === 'positive' ? 'default' :
                      callDetails.sentiment === 'negative' ? 'destructive' : 'secondary'
                    }>
                      {callDetails.sentiment}
                    </Badge>
                  </div>
                )}
                {callDetails.aiTone && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Tone:</span>
                    <Badge variant="outline">{callDetails.aiTone}</Badge>
                  </div>
                )}
                {callDetails.aiUrgency && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Urgency:</span>
                    <Badge variant={
                      callDetails.aiUrgency === 'high' ? 'destructive' :
                      callDetails.aiUrgency === 'medium' ? 'default' : 'secondary'
                    }>
                      {callDetails.aiUrgency}
                    </Badge>
                  </div>
                )}
                {callDetails.aiIntent && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Intent:</span>
                    <Badge variant="outline">{callDetails.aiIntent}</Badge>
                  </div>
                )}
              </div>
            )}

            {callDetails.aiAnalysisStatus === 'pending' && (
              <div className="bg-muted/50 border border-muted rounded-lg p-4 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">AI analysis is queued...</p>
              </div>
            )}

            {callDetails.aiAnalysisStatus === 'processing' && (
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-600" />
                <p className="text-sm text-blue-900 dark:text-blue-100">AI is analyzing the call...</p>
              </div>
            )}

            {callDetails.aiAnalysisStatus === 'failed' && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center justify-center mb-3">
                  <AlertCircle className="w-5 h-5 mr-2 text-destructive" />
                  <p className="text-sm text-destructive font-medium">AI analysis failed</p>
                </div>
                <Button 
                  onClick={handleRegenerateAnalysis} 
                  disabled={isRegenerating}
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  data-testid="button-retry-analysis"
                >
                  {isRegenerating ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-2" />
                      Retry Analysis
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Call Recording */}
            {callDetails.recordingUrl && (() => {
              const token = localStorage.getItem('token') || '';
              const recUrl = callDetails.recordingSid
                ? `/api/voice/recording/${callDetails.recordingSid}?token=${token}`
                : callDetails.recordingUrl;
              return (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Play className="w-5 h-5 text-primary" />
                    <p className="font-medium">Call Recording</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" asChild data-testid="button-play-recording">
                      <a href={recUrl} target="_blank" rel="noopener noreferrer">
                        <Play className="w-3 h-3 mr-1" />
                        Play
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild data-testid="button-download-recording">
                      <a href={recUrl} download>
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </a>
                    </Button>
                  </div>
                </div>
                {/* Audio player - use proxy with token for authenticated playback */}
                <audio controls className="w-full mt-3" src={recUrl}>
                  Your browser does not support the audio element.
                </audio>
              </div>
              );
            })()}

            {!callDetails.recordingUrl && (
              <div className="bg-muted/50 border border-muted rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">Recording is being processed...</p>
              </div>
            )}

            {/* Transcript */}
            {callDetails.transcript && (
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-2">Transcript</p>
                <div className="bg-muted p-3 rounded-md max-h-64 overflow-y-auto">
                  <p className="text-sm whitespace-pre-wrap">{callDetails.transcript}</p>
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="flex justify-end pt-2">
              <Button onClick={onClose} data-testid="button-close-summary-bottom">
                Got it!
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
